import { dayLabel } from '../format';

import type { NoteItem, ReplaceCategoryLine } from '@/store/api/mcpMemberApi';

export const sourceLabels = (
  item: NoteItem,
  now: Date = new Date(),
): string[] => {
  if (item.sources.length > 0)
    return item.sources.map((source) => source.label);
  if (item.origin === 'ai')
    return [`${dayLabel(item.updated_at, now)}, via your AI`];
  if (item.origin === 'member') return ['Added by you'];
  return [];
};

export const toEditText = (items: NoteItem[], now: Date = new Date()): string =>
  items
    .map((item) => {
      const labels = sourceLabels(item, now);
      return labels.length > 0
        ? `${item.text} [${labels.join(', ')}]`
        : item.text;
    })
    .join('\n');

export interface ParsedLine {
  labels: string[];
  text: string;
}

const LINE_WITH_SOURCES = /^(.*?)\s*\[([^\]]*[^\]\s][^\]]*)\]$/u;

// Prototype csave: trim, drop list bullets and blank lines, split "[a, b]".
export const parseEditText = (value: string): ParsedLine[] =>
  value
    .split('\n')
    .map((raw) => raw.trim().replace(/^[-·•]\s*/u, ''))
    .filter(Boolean)
    .map((line) => {
      const match = LINE_WITH_SOURCES.exec(line);
      if (!match) return { labels: [], text: line };
      return {
        labels: (match[2] ?? '')
          .split(',')
          .map((label) => label.trim())
          .filter(Boolean),
        text: (match[1] ?? '').trim(),
      };
    })
    .filter((line) => line.text.length > 0);

// Compare the whole bracket text: labels can contain commas themselves
// ("Nov 26, 2025", "Mar 2, via your AI"), so split labels never line up.
const sameLabels = (a: string[], b: string[]): boolean =>
  a.join(', ') === b.join(', ');

// Contracts §8.4: text never includes the [dates] suffix; unchanged lines keep
// their id; a line with an id and changed text is edited in place (sources kept);
// lines without an id are new; missing ids become tombstones. An edited line keeps
// its id when its bracketed sessions still match an unclaimed item.
export const toReplaceLines = (
  before: NoteItem[],
  parsed: ParsedLine[],
  now: Date = new Date(),
): ReplaceCategoryLine[] => {
  const unclaimed = new Set(before.map((item) => item.id));
  const matched: (NoteItem | undefined)[] = parsed.map(() => undefined);
  // Strongest evidence first, so a duplicate's unchanged line cannot take the
  // id of the note that was edited: text and sessions, then text alone, then
  // sessions alone (an edited line keeps its id and its session links).
  const passes: ((item: NoteItem, line: ParsedLine) => boolean)[] = [
    (item, line) =>
      item.text === line.text &&
      sameLabels(sourceLabels(item, now), line.labels),
    (item, line) => item.text === line.text,
    (item, line) =>
      line.labels.length > 0 &&
      sameLabels(sourceLabels(item, now), line.labels),
  ];
  passes.forEach((matches) => {
    parsed.forEach((line, index) => {
      if (matched[index]) return;
      const item = before.find(
        (candidate) => unclaimed.has(candidate.id) && matches(candidate, line),
      );
      if (!item) return;
      unclaimed.delete(item.id);
      matched[index] = item;
    });
  });
  return parsed.map((line, index) => {
    const match = matched[index];
    return match ? { id: match.id, text: line.text } : { text: line.text };
  });
};
